<template>
  <div>
    <b-row>
      <b-col>
        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              參數設定
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              輔翼 會員等級批量調整（逐筆同步）
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="mb-3">
          <div class="mb-3">
            <div>
              <h4 class="font-weight-bold mb-2">輔翼 會員等級批量調整（逐筆同步）</h4>
              <p class="mb-2 text-muted">
                上傳 Excel 後，系統會逐筆同步會員等級到輔翼。
              </p>
            </div>
          </div>

          <div class="import-form">
            <b-form-group label-for="level-file">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="field-label mb-0" for="level-file">Excel 檔案</label>
              </div>
              <b-form-file
                id="level-file"
                v-model="selectedFile"
                accept=".xlsx,.xls"
                placeholder="尚未選擇檔案"
                browse-text="瀏覽"
                plain
              ></b-form-file>
              <small class="form-text text-muted">
                格式固定為 4 欄：第 1 欄會員編號、第 2 欄等級代號、第 3 欄會籍開始日、第 4 欄會籍結束日。支援 xlsx / xls，可包含表頭。
              </small>
              <small class="form-text text-danger">
                請勿填入 內部系統會員編號 或 內部系統會員等級名稱。
              </small>
              <b-button
                class="p-0"
                variant="link"
                size="sm"
                @click="getSampleExcelFile(null, '/excel/flaps_level_change.xlsx')"
              >
                <i class="fa fa-file-text mr-1" aria-hidden="true"></i>
                下載範例檔案
              </b-button>
            </b-form-group>
          </div>

          <div class="d-flex justify-content-end">
            <b-button
              variant="primary"
              :disabled="!canSubmit"
              @click="handlePreviewSubmit"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              ></b-spinner>
              <i
                v-else
                class="mdi mdi-sync mr-1"
                aria-hidden="true"
              ></i>
              {{ isSubmitting ? '同步中...' : '開始同步' }}
            </b-button>
          </div>
        </b-card>

        <b-card v-if="showResult" class="mb-3">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
            <div>
              <h5 class="font-weight-bold mb-1">同步結果</h5>
              <p class="mb-0 text-muted">以下為同步統計與錯誤列。</p>
            </div>
          </div>

          <b-row class="mb-3">
            <b-col
              v-for="item in summaryItems"
              :key="item.label"
              cols="12"
              md="4"
              class="mb-2"
            >
              <div :class="['summary-item', `summary-item-${item.variant}`]">
                <div class="summary-label">{{ item.label }}</div>
                <div class="summary-value">{{ item.value }}</div>
                <div class="summary-description">{{ item.description }}</div>
              </div>
            </b-col>
          </b-row>

          <b-table
            v-if="errorRows.length > 0"
            responsive
            striped
            hover
            :items="errorRows"
            :fields="errorFields"
          >
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ getStatusText(data.item.status) }}
              </b-badge>
            </template>
          </b-table>

          <b-alert
            v-else
            show
            variant="success"
            class="mb-0"
          >
            本次同步沒有錯誤列。
          </b-alert>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { getSampleExcelFile } from "@/utils/dashboardUtils";
import flapsApi from "@/apis/flaps";

export default {
  name: "FlapsCustomerLevelChangeForm",

  data() {
    return {
      selectedFile: null,
      showResult: false,
      isSubmitting: false,
      summaryItems: [
        {
          label: "總筆數",
          value: 0,
          variant: "total",
          description: "有效匯入列總數",
        },
        {
          label: "成功",
          value: 0,
          variant: "success",
          description: "同步成功筆數",
        },
        {
          label: "失敗",
          value: 0,
          variant: "failed",
          description: "同步失敗筆數",
        },
      ],
      errorFields: [
        { key: "row_number", label: "Excel 列號" },
        { key: "member_outer_code", label: "會員編號" },
        { key: "level_code", label: "等級代號" },
        { key: "membership_start_at", label: "會籍開始日" },
        { key: "membership_end_at", label: "會籍結束日" },
        { key: "status", label: "狀態" },
        { key: "error_message", label: "錯誤訊息" },
      ],
      errorRows: [],
    };
  },

  computed: {
    selectedFileName() {
      return this.selectedFile ? this.selectedFile.name : null;
    },
    canSubmit() {
      return Boolean(this.selectedFile && !this.isSubmitting);
    },
  },

  mounted() {
    if (!this.checkPermission([consts.FLAPS_CUSTOMER_LEVEL_CHANGE])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33",
        })
        .then(() => {
          this.$router.push({
            name: "OrganizationVariableList",
          });
        });
    }
  },

  methods: {
    getSampleExcelFile,
    handlePreviewSubmit() {
      if (!this.isValidExcelFile(this.selectedFile)) {
        this.$swal.fire({
          type: "warning",
          title: "檔案格式錯誤",
          text: "僅支援 xlsx / xls 檔案",
        });
        return;
      }

      this.$swal
        .fire({
          type: "warning",
          title: "確認同步會員等級？",
          html: `
            <div class="text-left">
              <div class="mb-2">此操作會將檔案中的會員等級同步至輔翼，請確認檔案正確。</div>
              <div><strong>檔案：</strong>${this.selectedFileName}</div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "確認同步",
          cancelButtonText: "取消",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value || result.isConfirmed) {
            await this.importCustomerLevels();
          }
        });
    },
    async importCustomerLevels() {
      try {
        this.isSubmitting = true;
        this.showResult = false;

        const { data } = await flapsApi.customerLevelChange({
          file: this.selectedFile,
        });

        const result = data && data.data ? data.data : data;
        this.applyImportResult(result || {});
        this.showResult = true;

        this.$swal.fire({
          type: "success",
          title: "同步完成",
          text: "會員等級同步已完成，請查看下方結果。",
        });
      } catch (error) {
        this.$swal.fire({
          type: "error",
          title: "同步失敗",
          text: this.getErrorMessage(error),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    applyImportResult(result) {
      const summary = result.summary || {};
      this.summaryItems = [
        {
          label: "總筆數",
          value: summary.total_rows || 0,
          variant: "total",
          description: "有效匯入列總數",
        },
        {
          label: "成功",
          value: summary.success_rows || 0,
          variant: "success",
          description: "同步成功筆數",
        },
        {
          label: "失敗",
          value: summary.failed_rows || 0,
          variant: "failed",
          description: "同步失敗筆數",
        },
      ];
      this.errorRows = result.error_rows || [];
    },
    isValidExcelFile(file) {
      if (!file || !file.name) {
        return false;
      }

      return /\.(xlsx|xls)$/i.test(file.name);
    },
    getErrorMessage(error) {
      const responseData = error && error.response ? error.response.data : null;
      const statusCode = error && error.response ? error.response.status : null;

      if (!responseData) {
        return "同步失敗，請稍後再試";
      }

      if (responseData.errors) {
        return Object.keys(responseData.errors)
          .map((field) => responseData.errors[field].join("、"))
          .join("；");
      }

      if (responseData.message) {
        return responseData.message;
      }

      if (responseData.error) {
        return responseData.error;
      }

      if (statusCode === 422) {
        return "檔案內容無有效資料或格式不正確，請確認欄位與資料後重試";
      }

      if (statusCode === 400) {
        return "同步前置設定不足，請確認商戶或模組設定是否完整";
      }

      return "同步失敗，請稍後再試";
    },
    getStatusText(status) {
      const statusMap = {
        duplicate_ignored: "重複資料已略過",
        sync_failed: "同步失敗",
        invalid_input: "資料格式錯誤",
        invalid_membership_date: "會籍日期錯誤",
        customer_not_found: "找不到會員",
        invalid_level_code: "等級代號無效",
      };

      return statusMap[status] || status;
    },
    getStatusVariant(status) {
      const variantMap = {
        duplicate_ignored: "info",
        sync_failed: "danger",
        invalid_input: "warning",
        invalid_membership_date: "warning",
        customer_not_found: "secondary",
        invalid_level_code: "warning",
      };

      return variantMap[status] || "light";
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>

<style scoped lang="scss">
.import-form {
  max-width: 640px;
}

.field-label {
  color: #1f2933;
  font-size: 15px;
  font-weight: 700;
}

.summary-item {
  min-height: 74px;
  padding: 10px 14px;
  border: 1px solid #dbe4ea;
  border-radius: 4px;
  background: #ffffff;
}

.summary-item-total {
  border-left: 4px solid #607d8b;
}

.summary-item-success {
  border-left: 4px solid #2e7d32;
}

.summary-item-failed {
  border-left: 4px solid #c62828;
}

.summary-label {
  color: #52616b;
  font-size: 13px;
  font-weight: 700;
}

.summary-value {
  margin-top: 2px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.summary-item-success .summary-value {
  color: #2e7d32;
}

.summary-item-failed .summary-value {
  color: #c62828;
}

.summary-description {
  margin-top: 2px;
  color: #7b8794;
  font-size: 12px;
}
</style>
